import { useMutation } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useRef } from 'react';
import { acceptInvitation, isAuthFlagSet, setAuthFlag } from '../lib/api-client.js';
import { formatErrorMessage } from '../lib/error-messages.js';
import {
  clearPendingInviteToken,
  getPendingInviteToken,
  setPendingInviteToken,
} from '../lib/invite-token.js';

export function InviteAcceptPage({ token }: { token: string | undefined }) {
  const navigate = useNavigate();
  const startedRef = useRef(false);
  const authenticated = isAuthFlagSet();

  useEffect(() => {
    document.title = '招待を承認 - PetaCMS';
  }, []);

  useEffect(() => {
    if (token) setPendingInviteToken(token);
  }, [token]);

  const effectiveToken = useMemo(() => token ?? getPendingInviteToken() ?? undefined, [token]);

  const acceptMutation = useMutation({
    mutationFn: (inviteToken: string) => acceptInvitation(inviteToken),
    onSuccess: (data) => {
      setAuthFlag();
      clearPendingInviteToken();
      navigate({
        to: '/sites/$siteId/dashboard',
        params: { siteId: data.siteId },
      });
    },
    // PR-VERIFY-RESET-CLEANUP: 招待承諾失敗時も sessionStorage の token を破棄。
    // 残すと別タブや再 navigate で同 token を無限に試行し、server 側の rate-limit を
    // 食い潰すリスク + UI 上のループ表示の原因になる。
    onError: () => {
      clearPendingInviteToken();
    },
  });

  useEffect(() => {
    if (!authenticated || !effectiveToken || startedRef.current) return;
    startedRef.current = true;
    acceptMutation.mutate(effectiveToken);
  }, [acceptMutation, authenticated, effectiveToken]);

  if (!effectiveToken) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f8f7f6]">
        <div className="w-full max-w-[400px] rounded-[12px] border border-[#edebe8] bg-white px-8 py-10 text-center shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
          <h1 className="text-[28px] font-bold text-[#23221e]">PetaCMS</h1>
          <p className="mt-1 text-[13px] font-medium text-[#706d65]">招待リンク確認失敗</p>
          <p className="mt-4 text-[14px] text-[#706d65]">
            リンクが途中で切れている可能性があります。サイトのオーナーに再招待を依頼してください。
          </p>
          <div className="mt-6">
            <Link to="/login" className="text-sm font-medium text-[#3b82f6] hover:text-[#1d4ed8]">
              ログインページへ
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!authenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f8f7f6]">
        <div className="w-full max-w-[400px] rounded-[12px] border border-[#edebe8] bg-white px-8 py-10 text-center shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
          <h1 className="text-[28px] font-bold text-[#23221e]">PetaCMS</h1>
          <p className="mt-1 text-[13px] font-medium text-[#706d65]">招待を受ける</p>
          <span className="mt-6 block text-5xl">✉️</span>
          <p className="mt-4 text-[14px] text-[#706d65]">
            招待されたメールアドレスでログインしてください。未登録の場合は先にアカウントを作成します。
          </p>
          <div className="mt-8 space-y-3">
            <Link to="/login" className="block">
              <button
                type="button"
                className="h-[44px] w-full rounded-[6px] bg-[#3b82f6] text-sm font-medium text-white transition hover:bg-[#1d4ed8]"
              >
                ログインして参加する
              </button>
            </Link>
            <Link to="/register" className="block">
              <button
                type="button"
                className="h-[44px] w-full rounded-[6px] border border-[#d6d3d0] text-sm font-medium text-[#706d65] transition hover:bg-[#f8f7f6]"
              >
                新規登録する
              </button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const errorMessage = acceptMutation.error
    ? formatErrorMessage(acceptMutation.error, '招待の承諾に失敗しました')
    : '';

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#f8f7f6]">
      <div className="w-full max-w-[400px] rounded-[12px] border border-[#edebe8] bg-white px-8 py-10 text-center shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
        {acceptMutation.isError ? (
          <>
            <h1 className="text-[28px] font-bold text-[#23221e]">PetaCMS</h1>
            <p className="mt-4 text-[14px] font-medium text-[#991b1b]">
              招待を承諾できませんでした
            </p>
            <p className="mt-2 text-[14px] text-[#706d65]">{errorMessage}</p>
            <div className="mt-6">
              <Link to="/sites" className="text-sm font-medium text-[#3b82f6] hover:text-[#1d4ed8]">
                サイト一覧へ
              </Link>
            </div>
          </>
        ) : (
          <>
            <h1 className="text-[28px] font-bold text-[#23221e]">PetaCMS</h1>
            <p className="mt-1 text-[13px] font-medium text-[#706d65]">招待を承諾中</p>
            <p className="mt-4 text-[14px] text-[#706d65]">
              参加処理が完了すると自動でダッシュボードへ移動します。
            </p>
            <div className="mt-6 flex justify-center">
              <div
                className="h-2 w-2 animate-bounce rounded-full bg-[#3b82f6]"
                style={{ animationDelay: '0s' }}
              />
              <div
                className="mx-1 h-2 w-2 animate-bounce rounded-full bg-[#3b82f6]"
                style={{ animationDelay: '0.2s' }}
              />
              <div
                className="h-2 w-2 animate-bounce rounded-full bg-[#3b82f6]"
                style={{ animationDelay: '0.4s' }}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
